<template>
    <div class="gv-formula">
        <p id="gv-formula-help" class="gv-formula-help">
            Chaque sous-produit porte sa part du prix TTC<span v-if="!tvaDisabled">
                et sa propre TVA (par exemple le repas à 10 % et le vin à 20 %)</span
            >. Le prix de l'offre est la somme des sous-produits<span v-if="!tvaDisabled"> ; la facture détaille la TVA de chacun</span>.
        </p>
        <ShowErrors class="d-block mb-2" :errors="errors" errorKey="components" />
        <div class="gv-formula-head" aria-hidden="true">
            <span>Sous-produit</span>
            <span>Prix TTC</span>
            <span v-if="!tvaDisabled">TVA</span>
            <span></span>
        </div>
        <ol class="gv-formula-list" aria-describedby="gv-formula-help">
            <li v-for="(component, index) in components" :key="index" class="gv-formula-row" :class="{ 'without-vat': tvaDisabled }">
                <div>
                    <label class="gv-formula-label" :for="`gv-formula-name-${index}`">Nom du sous-produit {{ index + 1 }}</label>
                    <input
                        :id="`gv-formula-name-${index}`"
                        v-model="component.name"
                        type="text"
                        class="form-control"
                        :maxlength="nameMaxLength"
                        placeholder="Ex. : Dîner, Coupe de champagne" />
                    <ShowErrors :errors="errors" :errorKey="`components.${index}.name`" />
                </div>
                <div>
                    <label class="gv-formula-label" :for="`gv-formula-price-${index}`"
                        >Prix TTC<span class="visually-hidden"> du sous-produit {{ index + 1 }}, en euros</span></label
                    >
                    <div class="input-group">
                        <input
                            :id="`gv-formula-price-${index}`"
                            v-model="component.price"
                            type="number"
                            min="0"
                            step="0.01"
                            inputmode="decimal"
                            class="form-control" />
                        <span class="input-group-text">€</span>
                    </div>
                    <ShowErrors :errors="errors" :errorKey="`components.${index}.price`" />
                </div>
                <div v-if="!tvaDisabled">
                    <label class="gv-formula-label" :for="`gv-formula-tva-${index}`"
                        >TVA<span class="visually-hidden"> du sous-produit {{ index + 1 }}</span></label
                    >
                    <select :id="`gv-formula-tva-${index}`" v-model="component.tva" class="form-select">
                        <option :value="null">--</option>
                        <option v-for="rate in taxRates" :key="rate" :value="rate">{{ formatRate(rate) }} %</option>
                    </select>
                    <ShowErrors :errors="errors" :errorKey="`components.${index}.tva`" />
                </div>
                <button
                    type="button"
                    class="btn btn-sm btn-outline-secondary gv-formula-remove"
                    :disabled="components.length <= minComponents"
                    :aria-label="`Retirer le sous-produit ${index + 1}`"
                    @click="components.splice(index, 1)">
                    <feather type="trash-2" aria-hidden="true" />
                </button>
            </li>
        </ol>
        <button
            type="button"
            class="btn btn-sm btn-outline-success btn-circle"
            :disabled="components.length >= maxComponents"
            @click="components.push(emptyComponent())">
            <feather type="plus" aria-hidden="true" /> Ajouter un sous-produit
        </button>
        <dl class="gv-formula-summary" aria-live="polite">
            <div>
                <dt>Prix TTC de l'offre</dt>
                <dd>{{ formatCurrency(totalCents / 100) }}</dd>
            </div>
            <div v-for="line in vatLines" :key="line.rate">
                <dt>dont TVA {{ formatRate(line.rate) }} %</dt>
                <dd>{{ formatCurrency(line.amount / 100) }}</dd>
            </div>
        </dl>
    </div>
</template>

<script>
import ShowErrors from "../../errors/ShowErrors";
import {
    FORMULA_MAX_COMPONENTS,
    FORMULA_MIN_COMPONENTS,
    FORMULA_TAX_RATES,
    emptyComponent,
    formulaTotalCents,
    vatByRate,
} from "./formulaComposition.mjs";

export default {
    name: "GiftVoucherFormulaComponents",
    props: {
        components: {
            type: Array,
            required: true,
        },
        tvaDisabled: {
            type: Boolean,
            default: false,
        },
        errors: {
            default: null,
        },
    },
    data() {
        return {
            taxRates: FORMULA_TAX_RATES,
            minComponents: FORMULA_MIN_COMPONENTS,
            maxComponents: FORMULA_MAX_COMPONENTS,
            nameMaxLength: 100,
        };
    },
    computed: {
        totalCents() {
            return formulaTotalCents(this.components);
        },
        vatLines() {
            return this.tvaDisabled ? [] : vatByRate(this.components);
        },
    },
    methods: {
        emptyComponent,
        formatRate(rate) {
            return String(rate).replace(".", ",");
        },
    },
    components: {
        ShowErrors,
    },
};
</script>

<style scoped>
.gv-formula-help {
    margin-bottom: 0.75rem;
    color: #6b7680;
    font-size: 0.85rem;
}

.gv-formula-head,
.gv-formula-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 8.5rem 6.5rem 2.5rem;
    gap: 0.5rem;
    align-items: start;
}

.gv-formula-row.without-vat {
    grid-template-columns: minmax(0, 1fr) 8.5rem 2.5rem;
}

.gv-formula-head {
    margin-bottom: 0.25rem;
    color: #6b7680;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
}

.gv-formula-list {
    margin: 0 0 0.75rem;
    padding: 0;
    list-style: none;
}

.gv-formula-row {
    margin-bottom: 0.5rem;
}

.gv-formula-label {
    display: block;
    margin-bottom: 0.25rem;
    font-size: 0.8rem;
    font-weight: 500;
}

@media (min-width: 576px) {
    .gv-formula-label {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
}

.gv-formula-remove {
    min-height: 38px;
}

.gv-formula-remove :deep(svg) {
    width: 16px;
    height: 16px;
}

.gv-formula-summary {
    display: grid;
    gap: 0.2rem;
    margin: 1rem 0 0;
    padding: 0.75rem 1rem;
    border: 1px solid #e3e8ec;
    border-radius: 12px;
    background: #f8fafb;
}

.gv-formula-summary div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
}

.gv-formula-summary dt {
    font-weight: 500;
}

.gv-formula-summary dd {
    margin: 0;
    font-variant-numeric: tabular-nums;
}

.gv-formula-summary div:first-child {
    font-weight: 600;
}

@media (max-width: 575px) {
    .gv-formula-head {
        display: none;
    }

    .gv-formula-row,
    .gv-formula-row.without-vat {
        grid-template-columns: minmax(0, 1fr) 2.5rem;
        padding: 0.75rem;
        border: 1px solid #e3e8ec;
        border-radius: 12px;
    }

    .gv-formula-row > div:first-child {
        grid-column: 1 / -1;
    }

    .gv-formula-row > div:not(:first-child) {
        grid-column: 1;
    }

    .gv-formula-remove {
        grid-column: 2;
        grid-row: 2;
    }
}
</style>
